import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Overview from "./partials/Overview";
import Order from "./partials/Order";
import Email from "./partials/Email";
import Quote from "./partials/Quote";
import RAN from "./partials/RAN";
import Warranty from "./partials/Warranty";
import Voicemail from "./partials/Voicemail";

import {
  makeStyles,
  Image,
  Text,
  Tab,
  TabList,
  tokens,
  Button,
  Spinner,
  Select,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
} from "@fluentui/react-components";

const useStyles = makeStyles({
  root: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    padding: tokens.spacingHorizontalM,
    minHeight: "100vh",
    maxWidth: "100vw",
    wordWrap: "break-word",
    textWrap: "wrap",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: tokens.spacingHorizontalM,
  },
  logo: {
    marginRight: tokens.spacingHorizontalM,
  },
  select: {
    marginLeft: tokens.spacingHorizontalM,
  },
  panels: {
    paddingTop: tokens.spacingHorizontalM,
    width: "100%",
  },
  loading: {
    display: "flex",
    minHeight: "75vh",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    minHeight: "75vh",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    gap: tokens.spacingVerticalM,
    textAlign: "center",
  },
  loginPrompt: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalS,
    textAlign: "center",
  },
  text: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  textBlock: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    display: "block",
  },
  tablist: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  tabListWrapper: {
    overflow: "hidden",
    width: "100%",
  },
  overflowTab: {
    minWidth: "32px",
  },
  subtitle2: {
    display: "block",
    marginBottom: tokens.spacingVerticalM,
  },
  option: {
    fontSize: tokens.fontSizeBase200,
  },
  inline: {
    display: "inline",
  },
  logoutPanels: {
    display: "flex",
    justifyContent: "center",
    marginTop: "auto",
    paddingTop: tokens.spacingVerticalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    textAlign: "center",
    width: "100%",
  },
  logoutButton: {
    backgroundColor: "#d13438",
    color: "#ffffff",
    ":hover": {
      backgroundColor: "#b92b2f",
      color: "#ffffff",
    },
  },
});

const App = (props) => {
  const settings = props.settings ?? Office.context.roamingSettings;

  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState("order");
  const [overflowTabs, setOverflowTabs] = useState([]);
  const [isTabOverflowing, setIsTabOverflowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noQuery, setNoQuery] = useState(false);

  // For triggering useeffect
  const [poke, setPoke] = useState(true);

  const [selectOptions, setSelectOptions] = useState([]);

  const [overview, setOverview] = useState({});
  const [orders, setOrders] = useState(null);
  const [emails, setEmails] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [rans, setRans] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [authenticated, setAuthenticated] = useState(false);
  const [accessToken, setAccessTokenState] = useState("");
  const [refreshToken, setRefreshTokenState] = useState("");
  const accessTokenRef = React.useRef("");
  const refreshTokenRef = React.useRef("");

  const [tabOrderDisabled, setTabOrderDisabled] = useState(true);
  const [tabEmailDisabled, setTabEmailDisabled] = useState(true);
  const [tabQuoteDisabled, setTabQuoteDisabled] = useState(true);
  const [tabRANDisabled, setTabRANDisabled] = useState(true);
  const [tabWarrantyDisabled, setTabWarrantyDisabled] = useState(true);
  const [tabVoicemailDisabled, setTabVoicemailDisabled] = useState(true);

  const [overviewLoading, setOverviewLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [quotesLoading, setQuotesLoading] = useState(true);
  const [ransLoading, setRansLoading] = useState(true);
  const [warrantiesLoading, setWarrantiesLoading] = useState(true);
  const [voicemailsLoading, setVoicemailsLoading] = useState(true);
  const tabListRef = React.useRef(null);
  const tabRefs = React.useRef({});
  const moreTabRef = React.useRef(null);

  /* TEST */
  //let domain = 'https://www.bestpool.test';
  //let clientId = 'NjY3ZTA3NTU1OTM1MjVj';
  //let clientSecret = 'f3db1b6273808e79d57112739031ae3633f2f5cf';
  //let redirectUri = domain + '/oauth/office';

  /* LIVE */
  let domain = "https://www.bestpoolsupplies.com.au";
  let clientId = "NjZiZjYzMmM0OGRlZWYx";
  let clientSecret = "2af2d4e370fe0f56ab18172554f8b8cf840354ae";
  let redirectUri = domain + "/oauth/office";
  const isLocalhost = window.location.hostname === "localhost";
  const apiDomain = isLocalhost ? "" : domain;

  let dialog;

  console.log("window.navigator.appVersion", window.navigator.appVersion);

  useEffect(() => {
    const processQueryAsync = async () => {
      setLoading(true);
      setNoQuery(true);

      // Get token from storage when load
      try {
        await hydrateAuthState();
      } catch (error) {
        console.log("Cannot hydrate auth state", error);
      }

      if (!Office.context.mailbox.item) {
        setSelectOptions([]);
        setLoading(false);
        setNoQuery(true);
        return;
      }

      collectOptions(Office.context.mailbox.item)
        .then((options) => {
          processQuery(options[0] !== undefined ? options[0] : null);
        })
        .catch((error) => {
          console.log("Cannot collect options", error);
          setSelectOptions([]);
          setLoading(false);
          setNoQuery(true);
        });
    };
    // Run once
    processQueryAsync();

    // Set up the ItemChanged event.
    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, async () => {
      await processQueryAsync();
    });

    return () => {
      Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
    };
  }, [poke]);

  /**
   * [description]
   * @param  {[type]} e    [description]
   * @param  {[type]} data [description]
   * @return {[type]}      [description]
   */
  const onTabSelect = (e, data) => {
    if (data.value === "more") {
      return;
    }
    setSelectedTab(data.value);
  };

  const settingsCallback = (result, key) => {
    if (result.status == Office.AsyncResultStatus.Failed) {
      console.log("Settings callback - Failed for", key, result.error);
      return false;
    }
    return true;
  };

  const persistSetting = async (key, value) => {
    const safeValue = value ?? "";
    settings.set(key, safeValue);
    settings.saveAsync((result) => {
      if (!settingsCallback(result, key)) {
        console.log("Settings callback - failed for", key);
      }
    });

    if (OfficeRuntime?.storage?.setItem) {
      await OfficeRuntime.storage.setItem(key, safeValue);
    }

    window.localStorage.setItem(key, safeValue);
  };

  const readStoredSetting = async (key) => {
    const settingValue = settings.get(key);
    if (settingValue !== undefined && settingValue !== null && settingValue !== "") {
      return settingValue;
    }

    if (OfficeRuntime?.storage?.getItem) {
      const runtimeValue = await OfficeRuntime.storage.getItem(key);
      if (runtimeValue) {
        return runtimeValue;
      }
    }

    return window.localStorage.getItem(key) ?? "";
  };

  const getAccessToken = () => {
    return accessTokenRef.current;
  };

  const setAccessToken = async (token) => {
    const safeToken = token ?? "";
    accessTokenRef.current = safeToken;
    setAccessTokenState(safeToken);
    await persistSetting("BPSAccessToken", safeToken);
  };

  const getRefreshToken = () => {
    return refreshTokenRef.current;
  };

  const setRefreshToken = async (token) => {
    const safeToken = token ?? "";
    refreshTokenRef.current = safeToken;
    setRefreshTokenState(safeToken);
    await persistSetting("BPSRefreshToken", safeToken);
  };

  const hydrateAuthState = async () => {
    const storedAccessToken = await readStoredSetting("BPSAccessToken");
    const storedRefreshToken = await readStoredSetting("BPSRefreshToken");
    accessTokenRef.current = storedAccessToken ?? "";
    refreshTokenRef.current = storedRefreshToken ?? "";
    setAccessTokenState(storedAccessToken ?? "");
    setRefreshTokenState(storedRefreshToken ?? "");
    setAuthenticated(Boolean(storedAccessToken) || Boolean(storedRefreshToken));
  };

  /**
   * [description]
   * @return {[type]} [description]
   */
  const onLogin = async () => {
    let url = domain + "/oauth/authorize?response_type=code";
    url += "&client_id=" + clientId;
    url += "&redirect_uri=" + redirectUri;

    Office.context.ui.displayDialogAsync(url, { height: 50, width: 20 }, function (asyncResult) {
      if (asyncResult.value) {
        // Dialog instance
        dialog = asyncResult.value;

        // For windows
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args) => {
          // Code to execute when child sends message
          const code = args.message;

          // Get the token
          dialogMessageHandler(code);
        });

        // For mac
        if (window.navigator.appVersion.indexOf("Mac") !== -1) {
          window.addEventListener("message", async (args) => {
            // code to execute when child sends message
            const argsData = JSON.parse(args.data);
            const code = argsData.dialogMessage.messageContent;

            dialogMessageHandler(code);
          });
        }
      }
    });
  };

  const dialogMessageHandler = async (code) => {
    console.log("Got message from dialog", code);

    // Make sure we have the code
    if (code != "") {
      try {
        const tokenParams = new URLSearchParams({
          grant_type: "authorization_code",
          code: code,
          client_id: clientId,
          client_secret: clientSecret,
          redirect_uri: redirectUri,
        });

        const response = await axios.post(apiDomain + "/oauth/token.json", tokenParams, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          crossDomain: true,
        });

        console.log("Grant response", response);

        if (response.data.access_token) {
          await setAccessToken(response.data.access_token);
          console.log("Login - Access token set success", response.data.access_token);

          await setRefreshToken(response.data.refresh_token);
          console.log("Login - Refresh token set success", response.data.refresh_token);

          // Trigger change
          setPoke(!poke);
        } else {
          console.log("Cannot log you in. Please try again.");
        }
      } catch (error) {
        // We cannot catch the preflight error here
        // so if any error happened, we just assume it's
        // token expired
        console.log("sendRequest error", error);
      }

      dialog.close();
    } else {
      console.log("Failed");
      dialog.close();
    }
  };

  /**
   * [description]
   * @param  {[type]} email [description]
   * @return {[type]}       [description]
   */
  const isInternalEmail = (email) => {
    if (
      email.indexOf("bestpoolsupplies.com.au") !== -1 ||
      email.indexOf("3cx.com") !== -1 ||
      email.indexOf("3cx.net") !== -1 ||
      email.indexOf("members.ebay") !== -1
    ) {
      return true;
    }
    return false;
  };

  /**
   * [description]
   * @param  {[type]} recipients [description]
   * @return {[type]}            [description]
   */
  const filterInternalEmails = (recipients) => {
    var filtered = [];

    if (Array.isArray(recipients)) {
      recipients.forEach(function (recipient, index) {
        if (!isInternalEmail(recipient.emailAddress)) {
          filtered.push(recipient.emailAddress);
        }
      });
    } else if (typeof recipients === "string" || recipients instanceof String) {
      if (!isInternalEmail(recipients)) {
        filtered.push(recipients);
      }
    } else {
      if (!isInternalEmail(recipients.emailAddress)) {
        filtered.push(recipients.emailAddress);
      }
    }

    return filtered;
  };

  /**
   * [description]
   * @param  {[type]} content [description]
   * @return {[type]}         [description]
   */
  const stripTags = (content) => {
    // Replace all tags with whitespace
    return content.replace(/(<([^>]+)>)/gi, " ");
  };

  const filterNumber = (input) => {
    // Trim the number
    let number = input.trim();

    let numberWithCC = "";
    let numberWithPlus = "";
    let numberWithZero = "";

    if (number.substring(0, 1) == "0") {
      numberWithZero = number;
      numberWithPlus = "+61" + number.substring(1);
      numberWithCC = "61" + number.substring(1);
    } else if (number.substring(0, 2) == "61") {
      numberWithZero = "0" + number.substring(2);
      numberWithPlus = "+" + number;
      numberWithCC = number;
    } else if (number.substring(0, 3) == "+61") {
      numberWithZero = "0" + number.substring(3);
      numberWithPlus = number;
      numberWithCC = number.substring(1);
    } else {
      numberWithZero = number;
      numberWithPlus = number;
      numberWithCC = number;
    }

    return {
      numberWithZero: numberWithZero,
      numberWithPlus: numberWithPlus,
      numberWithCC: numberWithCC,
    };
  };

  /**
   * [description]
   * @param  {[type]} stripped [description]
   * @return {[type]}          [description]
   */
  const parseSubjectForOrderId = (stripped) => {
    var inline = null;

    // This is invoice
    if (stripped.indexOf("invoice #") >= 0) {
      var regex = /invoice #(.*)/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }

      // This is invoice
    } else if (stripped.indexOf("order #") >= 0) {
      var regex = /order #(.*)/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    } else if (stripped.indexOf("your item has sold") >= 0) {
      var regex = /buyer: (.*) the right way/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    }

    return inline;
  };

  /**
   * [description]
   * @param  {[type]} stripped [description]
   * @return {[type]}          [description]
   */
  const parseSubjectForRANId = (stripped) => {
    var inline = null;

    // This is invoice
    if (stripped.indexOf("ran (") >= 0) {
      var regex = /ran \((.*)\)/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }

      // This is invoice
    } else if (stripped.indexOf("ran(") >= 0) {
      var regex = /ran\((.*)\)/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    }

    return inline;
  };

  /**
   * [description]
   * @param  {[type]} stripped [description]
   * @return {[type]}          [description]
   */
  const parseSubjectForPurchaseOrderId = (stripped) => {
    var inline = null;

    // This is invoice
    if (stripped.indexOf("po#") >= 0) {
      var regex = /po#(.*)/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    } else if (stripped.indexOf("po #") >= 0) {
      var regex = /po #(.*)/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    }

    return inline;
  };

  /**
   * [description]
   * @param  {[type]} subject [description]
   * @return {[type]}         [description]
   */
  const parseSubject = (subject) => {
    var stripped = stripTags(subject);

    // To lowercase
    stripped = stripped.toLowerCase();

    // Remove new line
    stripped = stripped.replace(/(\r\n|\n|\r)/gm, "");

    // Remove double space
    stripped = stripped.replace(/\s\s+/g, " ");

    var orderId = parseSubjectForOrderId(stripped);
    var ranId = parseSubjectForRANId(stripped);
    var purchaseOrderId = parseSubjectForPurchaseOrderId(stripped);

    // Return
    return { orderId: orderId, ranId: ranId, purchaseOrderId: purchaseOrderId };
  };

  /**
   * [description]
   * @param  {[type]} stripped [description]
   * @return {[type]}          [description]
   */
  const parseBodyForInlinePhone = (stripped) => {
    var inline = [];

    var inline1 = stripped.match(/61[0-9]{9}/g);
    if (inline1 == null) {
      inline1 = [];
    }

    var inline2 = stripped.match(/04[0-9]{8}/g);
    if (inline2 == null) {
      inline2 = [];
    }

    // Merge
    inline = inline1.concat(inline2);
    inline = inline.filter((item, index) => {
      return inline.indexOf(item) === index;
    });

    return inline;
  };

  const parseBodyForInlineEmails = (stripped) => {
    var inline = [];
    var regex = /(^|[^a-zA-Z0-9._%+-])([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?=[^a-zA-Z0-9._%+-]|$)/g;
    var match;
    while ((match = regex.exec(stripped)) !== null) {
      inline.push(match[2]);
    }
    return inline;
  };

  const parseBodyForBracketedEmails = (rawBody) => {
    var inline = [];
    var regex = /(^|[^a-zA-Z0-9._%+-])<?([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})>?(?=[^a-zA-Z0-9._%+-]|$)/g;
    var match;
    while ((match = regex.exec(rawBody)) !== null) {
      inline.push(match[2]);
    }
    return inline;
  };

  /**
   * [description]
   * @param  {[type]} stripped [description]
   * @return {[type]}          [description]
   */
  const parseBodyForInlineOrderId = (stripped) => {
    var inline = null;

    // This is for item sold email
    if (stripped.indexOf("order:") >= 0) {
      var regex = /order: (.*) customer:/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    }

    return inline;
  };

  /**
   * [description]
   * @param  {[type]} stripped [description]
   * @return {[type]}          [description]
   */
  const parseBodyForInlineEbayUsername = (stripped) => {
    var inline = null;

    // This is for item sold email
    if (stripped.indexOf("your item sold") >= 0) {
      var regex = /leave feedback for (.*) postage/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    } else if (stripped.indexOf("your item has sold") >= 0) {
      var regex = /buyer: (.*) the right way/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }

      // This is for refund email
    } else if (stripped.indexOf("issuing a refund to") >= 0) {
      var regex = /issuing a refund to (.*) hi/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }

      // This is for another type of refund email
    } else if (stripped.indexOf("issue a refund by") >= 0) {
      var regex = /buyer id: (.*) return/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }

      // This is for another type of refund email
    } else if (stripped.indexOf("resolve this request by") >= 0) {
      var regex = /buyer: (.*) quantity/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    }

    if (inline === null) {
      var regexes = [
        /new message from[^a-z0-9]+([a-z0-9][a-z0-9._-]+)/,
        /from:\s*ebay\s*-\s*([a-z0-9][a-z0-9._-]+)/,
        /subject:\s*([a-z0-9][a-z0-9._-]+)\s*sent a message/,
        /sent a message about[^a-z0-9]+([a-z0-9][a-z0-9._-]+)/,
        /buyer id:\s*([a-z0-9][a-z0-9._-]+)/,
        /buyer:\s*([a-z0-9][a-z0-9._-]+)\b/,
      ];

      for (var i = 0; i < regexes.length; i++) {
        var match = regexes[i].exec(stripped);
        if (match && match[1]) {
          inline = match[1].trim();
          break;
        }
      }
    }

    return inline;
  };

  /**
   * [description]
   * @param  {[type]} stripped [description]
   * @return {[type]}          [description]
   */
  const parseBodyForInlineEbayOrderId = (stripped) => {
    var inline = null;

    // This is for item sold email
    if (stripped.indexOf("order number") >= 0) {
      var regex = /order number\s*:?\s*([0-9]{2}-[0-9]{5}-[0-9]{5})/;
      var match;

      if ((match = regex.exec(stripped)) != null) {
        if (match[1] !== undefined) {
          inline = match[1].trim();
        }
      }
    }

    if (inline === null) {
      var fallbackRegex = /order number\s*:?\s*([0-9-]{8,})/;
      var fallbackMatch;

      if ((fallbackMatch = fallbackRegex.exec(stripped)) != null) {
        if (fallbackMatch[1] !== undefined) {
          inline = fallbackMatch[1].trim();
        }
      }
    }

    return inline;
  };

  /**
   * [description]
   * @param  {[type]} body [description]
   * @return {[type]}      [description]
   */
  const parseBody = (body) => {
    var decodedBody = body.replaceAll(/&lt;/g, "<").replaceAll(/&gt;/g, ">");
    var stripped = stripTags(decodedBody);

    // To lower case
    stripped = stripped.toLowerCase();

    // Remove new line
    stripped = stripped.replaceAll(/(\r\n|\n|\r)/gm, "");

    // Remove double space
    stripped = stripped.replaceAll(/\s\s+/g, " ");

    var emailsFromText = parseBodyForInlineEmails(stripped);
    var emailsFromBracketed = parseBodyForBracketedEmails(decodedBody);
    var emails = emailsFromText
      .concat(emailsFromBracketed)
      .map((email) => email.toLowerCase())
      .filter((item, index, list) => list.indexOf(item) === index);
    var phones = parseBodyForInlinePhone(stripped);
    var orderId = parseBodyForInlineOrderId(stripped);
    var ebayUsername = parseBodyForInlineEbayUsername(stripped);
    var ebayOrderId = parseBodyForInlineEbayOrderId(stripped);

    // Return
    return { emails: emails, phones: phones, orderId: orderId, ebayUsername: ebayUsername, ebayOrderId: ebayOrderId };
  };

  /**
   * [description]
   * @return {[type]} [description]
   */
  const disableAllTabs = () => {
    // Reset tab selection
    setSelectedTab("order");

    // Disable all tabs
    setTabOrderDisabled(true);
    setTabEmailDisabled(true);
    setTabQuoteDisabled(true);
    setTabRANDisabled(true);
    setTabWarrantyDisabled(true);
    setTabVoicemailDisabled(true);
  };

  /**
   * [description]
   * @return {[type]} [description]
   */
  const emptyAllData = () => {
    // Set all data empty
    setOverview({});
    setOrders(null);
    setEmails([]);
    setQuotes([]);
    setRans([]);
    setWarranties([]);
    setVoicemails([]);
  };

  /**
   * [description]
   * @return {[type]} [description]
   */
  const allLoading = () => {
    setOverviewLoading(true);
    setOrdersLoading(true);
    setEmailsLoading(true);
    setQuotesLoading(true);
    setRansLoading(true);
    setWarrantiesLoading(true);
    setVoicemailsLoading(true);
  };

  /**
   * [description]
   * @param  {[type]} ev   [description]
   * @param  {[type]} data [description]
   * @return {[type]}      [description]
   */
  const queryOnSelect = (ev, data) => {
    const selected = selectOptions.find((search) => {
      return search.value == data.value;
    });

    return processQuery(selected);
  };

  /**
   * [description]
   * @param  {[type]} item [description]
   * @return {[type]}      [description]
   */
  const collectOptions = (item) => {
    return new Promise((resolve, reject) => {
      // Assuming that item is always a read item (instead of a compose item).
      if (item != null) {
        let options = [];

        let from = filterInternalEmails(item.from);
        from.forEach((emailAddress) => {
          options.push({
            type: "email",
            value: emailAddress,
          });
        });

        let to = filterInternalEmails(item.to);
        to.forEach((emailAddress) => {
          options.push({
            type: "email",
            value: emailAddress,
          });
        });

        let cc = filterInternalEmails(item.cc);
        cc.forEach((emailAddress) => {
          options.push({
            type: "email",
            value: emailAddress,
          });
        });

        let bcc = filterInternalEmails(item.bcc);
        bcc.forEach((recipient) => {
          options.push({
            type: "email",
            value: recipient,
          });
        });

        let subject = item.subject;
        if (subject != "") {
          const dataFromSubject = parseSubject(subject);

          // Get order id
          if (dataFromSubject.orderId) {
            options.push({
              type: "order_id",
              value: dataFromSubject.orderId,
            });
          }
        }

        const extractBodyOptions = (bodyValue) => {
          // Data from body
          const dataFromBody = parseBody(bodyValue);

          // Get emails
          if (dataFromBody.emails.length) {
            dataFromBody.emails.forEach((email) => {
              var filteredEmail = filterInternalEmails(email);
              if (filteredEmail.length != 0 && options.some((e) => e.type == "email" && e.value == email) == false) {
                options.push({
                  type: "email",
                  value: email,
                });
              }
            });
          }

          // Get phones
          if (dataFromBody.phones.length) {
            dataFromBody.phones.forEach((phone) => {
              options.push({
                type: "phone",
                value: phone,
              });
            });
          }

          // Get order id if above got nothing
          if (dataFromBody.orderId) {
            options.push({
              type: "order_id",
              value: dataFromBody.orderId,
            });
          }

          // Ebay username
          if (dataFromBody.ebayUsername) {
            options.push({
              type: "ebay_username",
              value: dataFromBody.ebayUsername,
            });
          }

          // Ebay order id
          if (dataFromBody.ebayOrderId) {
            options.push({
              type: "ebay_order_id",
              value: dataFromBody.ebayOrderId,
            });
          }
        };

        const finishResolve = () => {
          setSelectOptions(options);
          return resolve(options);
        };

        const tryResolveWithBody = (bodyValue) => {
          if (bodyValue && bodyValue.trim() !== "") {
            extractBodyOptions(bodyValue);
            finishResolve();
            return true;
          }

          return false;
        };

        item.body.getAsync(Office.CoercionType.Html, (bodyResult) => {
          if (bodyResult.status === Office.AsyncResultStatus.Succeeded && tryResolveWithBody(bodyResult.value)) {
            return;
          }

          item.body.getAsync(Office.CoercionType.Text, (textResult) => {
            if (textResult.status === Office.AsyncResultStatus.Succeeded && tryResolveWithBody(textResult.value)) {
              return;
            }

            console.log("Cannot get body of email.");
            return finishResolve();
          });
        });
      } else {
        console.log("No email selected.");
        return resolve([]);
      }
    });
  };

  /**
   * [description]
   * @param  {[type]} selected [description]
   * @return {[type]}          [description]
   */
  const processQuery = async (selected) => {
    // Main loading
    setLoading(true);

    // Empty all data
    emptyAllData();

    // Disable all tabs
    disableAllTabs();

    // Set all loading
    allLoading();

    // If no selected, try from selectOptions state
    if (selected === undefined) {
      selected = selectOptions[0] !== undefined ? selectOptions[0] : null;
    }

    // If still no selected, set no query and loading
    if (selected == null) {
      // We have no query
      setNoQuery(true);
      setLoading(false);
      throw new Error("No query available");
    }

    let query;
    let tasks = [];

    if (selected.type == "email") {
      // Format query
      query = {
        emails: [selected.value],
      };

      tasks.push(
        getOrdersByEmailOrPhone(query)
          .then(async (orders) => {
            // We got something for orders
            // Enable tab
            if (orders !== undefined && orders.length) {
              setTabOrderDisabled(false);
            }

            setOrders(orders);
            setOrdersLoading(false);

            // Get overview data from orders
            try {
              const overview = await getOverviewFromOrdersAndQuery(orders, query);
              setOverview(overview);
            } catch (error) {
              console.log("Cannot get overview from orders and query.", query, error);
            }

            // Get phone first then process voicemails
            try {
              const phones = await getPhonesFromOrders(orders);
              let subquery = {
                phones: phones,
              };

              try {
                const resultVoicemails = await getVoicemailByPhone(subquery);
                setVoicemailsLoading(false);

                // Enable tab
                if (resultVoicemails !== undefined && resultVoicemails.length) {
                  setTabVoicemailDisabled(false);
                }

                setVoicemails(resultVoicemails);
              } catch (error) {
                setVoicemailsLoading(false);
                console.log("Cannot get email by phone.", error);
                throw error;
              }
            } catch (error) {
              console.log("Cannot get phones from orders", query);
            }
          })
          .catch((error) => {
            // We got something for orders
            setOrdersLoading(false);
            console.log("Cannot get orders by email.", error);
            throw error;
          })
      );

      tasks.push(
        getEmailByEmail(query)
          .then((resultEmails) => {
            setEmailsLoading(false);

            // Enable tab
            if (resultEmails !== undefined && resultEmails.length) {
              setTabEmailDisabled(false);
            }

            setEmails(resultEmails);
          })
          .catch((error) => {
            setEmailsLoading(false);
            console.log("Cannot get quote by email.", error);
            throw error;
          })
      );

      tasks.push(
        getQuoteByEmail(query)
          .then((quotes) => {
            setQuotesLoading(false);

            // Enable tab
            if (quotes !== undefined && quotes.length) {
              setTabQuoteDisabled(false);
            }

            setQuotes(quotes);
          })
          .catch((error) => {
            setQuotesLoading(false);
            console.log("Cannot get quote by email.", error);
            throw error;
          })
      );

      tasks.push(
        getRANsByEmail(query)
          .then((rans) => {
            setRansLoading(false);

            // Enable tab
            if (rans !== undefined && rans.length) {
              setTabRANDisabled(false);
            }

            setRans(rans);
          })
          .catch((error) => {
            setRansLoading(false);
            console.log("Cannot get RANs by email.", error);
            throw error;
          })
      );

      tasks.push(
        getWarrantyByEmail(query)
          .then((warranties) => {
            setWarrantiesLoading(false);

            // Enable tab
            if (warranties !== undefined && warranties.length) {
              setTabWarrantyDisabled(false);
            }

            setWarranties(warranties);
          })
          .catch((error) => {
            setWarrantiesLoading(false);
            console.log("Cannot get warranty by email.", error);
            throw error;
          })
      );
    } else if (selected.type == "phone") {
      // Format query
      query = {
        phones: [selected.value],
      };

      tasks.push(
        getOrdersByEmailOrPhone(query)
          .then(async (orders) => {
            // We got something for orders
            // Enable tab
            if (orders !== undefined && orders.length) {
              setTabOrderDisabled(false);
            }

            setOrders(orders);
            setOrdersLoading(false);

            // Get overview data from orders
            try {
              const overview = await getOverviewFromOrdersAndQuery(orders, query);
              setOverview(overview);
            } catch (error) {
              console.log("Cannot get overview from orders and query.", query, error);
            }

            // Get emails from order
            try {
              const emails = await getEmailsFromOrders(orders);
              let subquery = {
                emails: emails,
              };

              await Promise.all([
                getEmailByEmail(subquery)
                  .then((resultEmails) => {
                    setEmailsLoading(false);

                    // Enable tab
                    if (resultEmails !== undefined && resultEmails.length) {
                      setTabEmailDisabled(false);
                    }

                    setEmails(resultEmails);
                  })
                  .catch((error) => {
                    setEmailsLoading(false);
                    console.log("Cannot get email by email.", error);
                    throw error;
                  }),
                getQuoteByEmail(subquery)
                  .then((quotes) => {
                    setQuotesLoading(false);

                    // Enable tab
                    if (quotes !== undefined && quotes.length) {
                      setTabQuoteDisabled(false);
                    }

                    setQuotes(quotes);
                  })
                  .catch((error) => {
                    setQuotesLoading(false);
                    console.log("Cannot get quote by email.", error);
                    throw error;
                  }),
                getRANsByEmail(subquery)
                  .then((rans) => {
                    setRansLoading(false);

                    // Enable tab
                    if (rans !== undefined && rans.length) {
                      setTabRANDisabled(false);
                    }

                    setRans(rans);
                  })
                  .catch((error) => {
                    setRansLoading(false);
                    console.log("Cannot get RANs by email.", error);
                    throw error;
                  }),
                getWarrantyByEmail(subquery)
                  .then((warranties) => {
                    setWarrantiesLoading(false);

                    // Enable tab
                    if (warranties !== undefined && warranties.length) {
                      setTabWarrantyDisabled(false);
                    }

                    setWarranties(warranties);
                  })
                  .catch((error) => {
                    setWarrantiesLoading(false);
                    console.log("Cannot get warranty by email.", error);
                    throw error;
                  }),
              ]);
            } catch (error) {
              console.log("Cannot get emails from orders", query);
              throw error;
            }
          })
          .catch((error) => {
            // We got something for orders
            setOrdersLoading(false);
            console.log("Cannot get orders by email.", error);
            throw error;
          })
      );

      tasks.push(
        getVoicemailByPhone(query)
          .then((resultVoicemails) => {
            setVoicemailsLoading(false);

            // Enable tab
            if (resultVoicemails !== undefined && resultVoicemails.length) {
              setTabVoicemailDisabled(false);
            }

            setVoicemails(resultVoicemails);
          })
          .catch((error) => {
            setVoicemailsLoading(false);
            console.log("Cannot get email by phone.", error);
            throw error;
          })
      );
    } else if (selected.type == "ebay_username" || selected.type == "ebay_order_id" || selected.type == "order_id") {
      // Ebay username
      if (selected.type == "ebay_username") {
        query = {
          ebay_username: selected.value,
        };

        // Ebay order id
      } else if (selected.type == "ebay_order_id") {
        query = {
          ebay_order_id: selected.value,
        };

        // Order id
      } else if (selected.type == "order_id") {
        query = {
          order_id: selected.value,
        };
      }

      tasks.push(
        getOrdersByIdOrEbayUsernameOrEbayOrderId(query)
          .then(async (orders) => {
            console.log("by id/ebay_username/ebay_order_id, get order", orders);

            try {
              const emails = await getEmailsFromOrders(orders);
              let virtualSelected = {
                type: "email",
                value: emails[0],
              };
              await processQuery(virtualSelected);
            } catch (error) {
              console.log("Cannot get emails from orders, try phone instead", error);

              try {
                const phones = await getPhonesFromOrders(orders);
                let virtualSelected = {
                  type: "phone",
                  value: phones[0],
                };
                await processQuery(virtualSelected);
              } catch (phoneError) {
                console.log("Cannot get phones from orders, giving up", phoneError);
                throw phoneError;
              }
            }
          })
          .catch((error) => {
            // We got something for orders
            setOrdersLoading(false);
            console.log("Cannot get orders by id/ebay_username/ebay_order_id.", error);
            throw error;
          })
      );
    } else {
      console.log("unknown type", selected);
    }

    // We have query
    setNoQuery(false);
    setLoading(false);

    const results = await Promise.allSettled(tasks);

    const rejected = results.find((result) => result.status === "rejected");
    if (rejected) {
      throw rejected.reason;
    }

    return true;
  };

  /**
   * [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const buildQuery = (query) => {
    let data = {
      _method: "POST",
    };

    if (query.emails !== undefined) {
      data.emails = query.emails;
    }

    if (query.phones !== undefined) {
      data.phones = query.phones;
    }

    if (query.order_id !== undefined) {
      data.id = query.order_id;
    }

    if (query.ebay_username !== undefined) {
      data.ebay = query.ebay_username;
    }

    if (query.ebay_order_id !== undefined) {
      data.ebay_order_id = query.ebay_order_id;
    }

    return data;
  };

  /**
   * [description]
   * @param  {[type]} url   [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const sendRequest = async (url, query) => {
    // Build query
    const data = buildQuery(query);

    // Get token from local, faster than state
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    try {
      return await axios.post(url, data, {
        crossDomain: true,
        headers: {
          Authorization: "Bearer " + accessToken,
        },
      });
    } catch (error) {
      // We cannot catch the preflight error here
      // so if any error happened, we just assume it's
      // token expired
      console.log("sendRequest error, try refreshing token", error);

      try {
        const refreshParams = new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: refreshToken,
          client_id: clientId,
          client_secret: clientSecret,
        });

        const response = await axios.post(apiDomain + "/oauth/token.json", refreshParams, {
          crossDomain: true,
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });

        if (response?.data?.access_token) {
          await setAccessToken(response.data.access_token);
        }
        if (response?.data?.refresh_token) {
          await setRefreshToken(response.data.refresh_token);
        }

        return await sendRequest(url, query);
      } catch (refreshError) {
        console.log("Refreshing token error", refreshError);
        await clearAuthState();
        throw refreshError;
      }
    }
  };

  /**
   * [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const getOrdersByEmailOrPhone = (query) => {
    return new Promise((resolve, reject) => {
      // Url for this operation
      const url = apiDomain + "/admin/crm/orders.json";

      sendRequest(url, query)
        .then((response) => {
          return resolve(response.data.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  };

  /**
   * [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const getOrdersByIdOrEbayUsernameOrEbayOrderId = (query) => {
    return new Promise((resolve, reject) => {
      // Url for this operation
      const url = apiDomain + "/admin/crm/orders.json";

      sendRequest(url, query)
        .then((response) => {
          return resolve(response.data.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  };

  /**
   * [description]
   * @param  {[type]} orders [description]
   * @param  {[type]} query  [description]
   * @return {[type]}        [description]
   */
  const getOverviewFromOrdersAndQuery = (orders, query) => {
    return new Promise((resolve, reject) => {
      if (!orders || orders.length == 0) {
        return reject("Cannot build overview without orders");
      }

      // Pick one
      const order = orders[0];

      // Define the container
      let overview = {};

      // Add properties
      Object.defineProperty(overview, "name", { value: order.Order.name, writable: true });
      Object.defineProperty(overview, "email", { value: order.Order.email, writable: true });
      Object.defineProperty(overview, "phone", { value: order.Order.phone, writable: true });
      Object.defineProperty(overview, "ebay_username", { value: query.ebay_username, writable: true });
      Object.defineProperty(overview, "ebay_data", { value: order.Order.ebay_data, writable: true });

      Object.defineProperty(overview, "billing_address_1", { value: order.Order.billing_address_1, writable: true });
      Object.defineProperty(overview, "billing_address_2", { value: order.Order.billing_address_2, writable: true });
      Object.defineProperty(overview, "billing_suburb", { value: order.Order.billing_suburb, writable: true });
      Object.defineProperty(overview, "billing_city", { value: order.Order.billing_city, writable: true });
      Object.defineProperty(overview, "billing_postcode", { value: order.Order.billing_postcode, writable: true });
      Object.defineProperty(overview, "billing_state", { value: order.Order.billing_state, writable: true });
      Object.defineProperty(overview, "billing_country_id", { value: order.Order.billing_country_id, writable: true });
      Object.defineProperty(overview, "billing_country", { value: order.Order.billing_country, writable: true });

      if (order.Order.same_shipping) {
        Object.defineProperty(overview, "shipping_address_1", {
          value: order.Order.billing_address_1,
          writable: true,
        });
        Object.defineProperty(overview, "shipping_address_2", {
          value: order.Order.billing_address_2,
          writable: true,
        });
        Object.defineProperty(overview, "shipping_suburb", { value: order.Order.billing_suburb, writable: true });
        Object.defineProperty(overview, "shipping_city", { value: order.Order.billing_city, writable: true });
        Object.defineProperty(overview, "shipping_postcode", { value: order.Order.billing_postcode, writable: true });
        Object.defineProperty(overview, "shipping_state", { value: order.Order.billing_state, writable: true });
        Object.defineProperty(overview, "shipping_country_id", {
          value: order.Order.billing_country_id,
          writable: true,
        });
        Object.defineProperty(overview, "shipping_country", { value: order.Order.billing_country, writable: true });
      } else {
        Object.defineProperty(overview, "shipping_address_1", {
          value: order.Order.shipping_address_1,
          writable: true,
        });
        Object.defineProperty(overview, "shipping_address_2", {
          value: order.Order.shipping_address_2,
          writable: true,
        });
        Object.defineProperty(overview, "shipping_suburb", { value: order.Order.shipping_suburb, writable: true });
        Object.defineProperty(overview, "shipping_city", { value: order.Order.shipping_city, writable: true });
        Object.defineProperty(overview, "shipping_postcode", { value: order.Order.shipping_postcode, writable: true });
        Object.defineProperty(overview, "shipping_state", { value: order.Order.shipping_state, writable: true });
        Object.defineProperty(overview, "shipping_country_id", {
          value: order.Order.shipping_country_id,
          writable: true,
        });
        Object.defineProperty(overview, "shipping_country", { value: order.Order.shipping_country, writable: true });
      }

      return resolve(overview);
    });
  };

  /**
   * [description]
   * @param  {[type]} orders [description]
   * @return {[type]}        [description]
   */
  const getPhonesFromOrders = (orders) => {
    return new Promise((resolve, reject) => {
      if (!orders || orders.length == 0) {
        return reject("Cannot get phones, empty orders");
      }

      // Pick one
      const order = orders[0];

      // Define the container
      let phones = [];

      if (order.Order.phone != "") {
        phones.push(order.Order.phone);
      }

      if (order.Order.phone2 != "") {
        phones.push(order.Order.phone2);
      }

      return resolve(phones);
    });
  };

  /**
   * [description]
   * @param  {[type]} orders [description]
   * @return {[type]}        [description]
   */
  const getEmailsFromOrders = (orders) => {
    return new Promise((resolve, reject) => {
      if (!orders || orders.length == 0) {
        console.log("Cannot get emails, empty orders");
        return reject("Cannot get emails, empty orders");
      }

      // Define the container
      let emails = [];

      // Run each orders, collect, and filter
      orders.forEach((order) => {
        if (order.Order.email != "" && !isInternalEmail(order.Order.email)) {
          emails.push(order.Order.email);
        }
      });

      if (emails.length == 0) {
        return reject("No emails found");
      }

      return resolve(emails);
    });
  };

  /**
   * [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const getQuoteByEmail = (query) => {
    return new Promise((resolve, reject) => {
      // Url for this operation
      const url = apiDomain + "/admin/crm/quotes.json";

      sendRequest(url, query)
        .then((response) => {
          return resolve(response.data.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  };

  /**
   * [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const getRANsByEmail = (query) => {
    return new Promise((resolve, reject) => {
      // Url for this operation
      const url = apiDomain + "/admin/crm/rans.json";

      sendRequest(url, query)
        .then((response) => {
          return resolve(response.data.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  };

  /**
   * [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const getWarrantyByEmail = (query) => {
    return new Promise((resolve, reject) => {
      // Url for this operation
      const url = apiDomain + "/admin/crm/warranty.json";

      sendRequest(url, query)
        .then((response) => {
          return resolve(response.data.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  };

  /**
   * [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const getVoicemailByPhone = (query) => {
    return new Promise((resolve, reject) => {
      // Url for this operation
      const url = apiDomain + "/admin/crm/voicemails.json";

      sendRequest(url, query)
        .then((response) => {
          console.log("voicemails", response.data);
          return resolve(response.data.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  };

  /**
   * [description]
   * @param  {[type]} query [description]
   * @return {[type]}       [description]
   */
  const getEmailByEmail = (query) => {
    return new Promise((resolve, reject) => {
      // Url for this operation
      const url = apiDomain + "/admin/crm/mails.json";

      sendRequest(url, query)
        .then((response) => {
          return resolve(response.data.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  };

  /**
   * [description]
   * @param  {[type]} underscore [description]
   * @return {[type]}            [description]
   */
  const underscoreToWords = (underscore) => {
    return underscore.replace("_", " ");
  };

  const clearAuthState = async () => {
    await setAccessToken("");
    await setRefreshToken("");
    if (OfficeRuntime?.storage?.removeItem) {
      await OfficeRuntime.storage.removeItem("BPSAccessToken");
      await OfficeRuntime.storage.removeItem("BPSRefreshToken");
    }
    window.localStorage.removeItem("BPSAccessToken");
    window.localStorage.removeItem("BPSRefreshToken");
    setAuthenticated(false);
    setPoke(!poke);
  };

  const onLogout = async () => {
    await clearAuthState();
  };

  const tabs = [
    { value: "order", label: "Orders", disabled: tabOrderDisabled },
    { value: "email", label: "Email", disabled: tabEmailDisabled },
    { value: "quote", label: "Quote", disabled: tabQuoteDisabled },
    { value: "ran", label: "RAN", disabled: tabRANDisabled },
    { value: "warranty", label: "Warranty", disabled: tabWarrantyDisabled },
    { value: "voicemail", label: "Voicemail", disabled: tabVoicemailDisabled },
  ];

  const computeTabOverflow = React.useCallback(() => {
    const container = tabListRef.current;
    if (!container) {
      return;
    }

    const measureHiddenTabs = (reservedWidth) => {
      const availableWidth = container.clientWidth - reservedWidth;
      return tabs
        .filter((tab) => {
          const tabElement = tabRefs.current[tab.value];
          if (!tabElement) {
            return false;
          }
          return tabElement.offsetLeft + tabElement.offsetWidth > availableWidth;
        })
        .map((tab) => tab.value);
    };

    const hiddenWithoutReserve = measureHiddenTabs(0);
    if (hiddenWithoutReserve.length === 0) {
      setIsTabOverflowing(false);
      setOverflowTabs([]);
      return;
    }

    const moreTabWidth = moreTabRef.current ? moreTabRef.current.offsetWidth : 32;
    const hiddenWithReserve = measureHiddenTabs(moreTabWidth);
    setIsTabOverflowing(true);
    setOverflowTabs(hiddenWithReserve);
  }, [
    tabOrderDisabled,
    tabEmailDisabled,
    tabQuoteDisabled,
    tabRANDisabled,
    tabWarrantyDisabled,
    tabVoicemailDisabled,
  ]);

  React.useLayoutEffect(() => {
    computeTabOverflow();
    const resizeObserver = new ResizeObserver(() => {
      computeTabOverflow();
    });

    if (tabListRef.current) {
      resizeObserver.observe(tabListRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [computeTabOverflow]);

  const hasAccessToken = Boolean(accessToken);
  const hasSelectableOptions = selectOptions.length > 0;
  const hasDataResults = [orders, emails, quotes, rans, warranties, voicemails].some(
    (entries) => entries && entries.length
  );
  const hasOverviewData = overview && Object.keys(overview).length > 0;
  const anyDataLoading =
    ordersLoading ||
    emailsLoading ||
    quotesLoading ||
    ransLoading ||
    warrantiesLoading ||
    voicemailsLoading;
  const noDataFound =
    hasAccessToken && !loading && !noQuery && hasSelectableOptions && !hasDataResults && !hasOverviewData && !anyDataLoading;

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <Image src="/assets/BPS_Logo_32.png" className={styles.logo} />
        {hasAccessToken && !noQuery && (
          <Select className={styles.select} size="small" onChange={queryOnSelect}>
            {selectOptions.length ? (
              selectOptions.map((option) => (
                <option key={option.value} value={option.value} type={option.type}>
                  {option.value + " (" + underscoreToWords(option.type) + ")"}
                </option>
              ))
            ) : (
              <option value="" type="">
                No data found
              </option>
            )}
          </Select>
        )}
      </header>
      {hasAccessToken ? (
        loading == false ? (
          noQuery == false ? (
            noDataFound ? (
              <div className={styles.emptyState}>
                <Text>No data found</Text>
              </div>
            ) : (
              <>
                <Overview data={overview} />
                <div className={styles.tabListWrapper} ref={tabListRef}>
                  <TabList
                    defaultSelectedValue="order"
                    selectTabOnFocus={true}
                    selected={isTabOverflowing ? "more" : selectedTab}
                    onTabSelect={onTabSelect}
                    size="small"
                  >
                    {tabs.map((tab) => (
                      <Tab
                        key={tab.value}
                        value={tab.value}
                        className={styles.tablist}
                        disabled={tab.disabled}
                        ref={(node) => {
                          tabRefs.current[tab.value] = node;
                        }}
                      >
                        {tab.label}
                      </Tab>
                    ))}
                    {isTabOverflowing && (
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Tab
                            value="more"
                            className={mergeClasses(styles.tablist, styles.overflowTab)}
                            ref={moreTabRef}
                          >
                            ...
                          </Tab>
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            {overflowTabs.map((tabValue) => {
                              const tab = tabs.find((entry) => entry.value === tabValue);
                              if (!tab) {
                                return null;
                              }
                              return (
                                <MenuItem key={tab.value} onClick={() => setSelectedTab(tab.value)}>
                                  {tab.label}
                                </MenuItem>
                              );
                            })}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    )}
                  </TabList>
                </div>
                <div className={styles.panels}>
                  {selectedTab === "order" && <Order data={orders} domain={domain} loading={ordersLoading} />}
                  {selectedTab === "email" && <Email data={emails} loading={emailsLoading} sendEmail={props.sendEmail} />}
                  {selectedTab === "quote" && <Quote data={quotes} domain={domain} loading={quotesLoading} />}
                  {selectedTab === "ran" && <RAN data={rans} domain={domain} loading={ransLoading} />}
                  {selectedTab === "warranty" && (
                    <Warranty data={warranties} domain={domain} loading={warrantiesLoading} />
                  )}
                  {selectedTab === "voicemail" && (
                    <Voicemail data={voicemails} domain={domain} loading={voicemailsLoading} />
                  )}
                </div>
              </>
            )
          ) : (
            <div className={styles.emptyState}>
              <Text>No parsable data available in the email</Text>
            </div>
          )
        ) : (
          <div className={styles.loading}>
            <Spinner label="Loading" />
          </div>
        )
      ) : (
        <div className={styles.loading}>
          <div className={styles.loginPrompt}>
            <Text>Please login with your BestPoolSupplies account</Text>
            <Button onClick={onLogin}>Login</Button>
          </div>
        </div>
      )}
      {hasAccessToken && (
        <div className={styles.logoutPanels}>
          <Button className={styles.logoutButton} onClick={onLogout}>
            Logout
          </Button>
        </div>
      )}
    </div>
  );
};

App.propTypes = {
  title: PropTypes.string,
};

export default App;
